import {
  ArrowRight,
  CloudUpload,
  Folder,
  MyFiles,
  RMpp,
} from '@remarkable/ark-icons';
import { Tag, Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { Subscription } from 'src/api/endpoints/storeApi.types';
import { URLS } from 'src/utils/urls/urls';

import { useAuth0 } from '@auth0/auth0-react';
import { Select, Trans } from '@lingui/react/macro';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { EXTERNAL_URLS } from 'src/utils/urls/externalUrls';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { userHelpers } from 'src/utils/userHelpers';
import { FlexDivider } from './FlexDivider';
import { Link2 } from './Link2';
import { LinkExternal } from './LinkExternal';
import { Sheet } from './Sheet';
import { StatusTag } from './StatusTag';

export const ActiveSubscriptionHero = ({
  activePaperTablets,
  subscription,
}: {
  activePaperTablets: number;
  subscription: Subscription;
}) => {
  const { user } = useAuth0();
  const featureFlags = useFeatureFlags();

  const hasEnterpriseSubscription =
    userHelpers.hasSubscription(user) && userHelpers.isOrganizationMember(user);

  const claims = useAccessTokenClaims();
  const canSeeEnterpriseBilling = userHelpers.hasPermissionReadSubscription(
    claims.data
  );

  const maxDeviceCount = deviceHelpers.getMaxDeviceCount(user);
  return (
    <Sheet
      className="flex flex-col justify-between gap-0 !px-0 !py-32 text-left lxl:flex-row"
      data-cy="hero-dashboard"
    >
      <div className="flex w-full flex-1 flex-col justify-between gap-16 px-24">
        <Typography variant="heading-subtle-xl">
          <span className="flex items-center gap-8">
            <RMpp className="text-muted" size={24} />
            <Trans>Paper tablet</Trans>
          </span>
        </Typography>
        <Tag>
          <Trans context="active devices count vs max allowed devices count">
            {activePaperTablets} / {maxDeviceCount} Devices paired
          </Trans>
        </Tag>
        <Typography variant="body-sm" className="text-muted">
          <Trans>Add or edit devices connected to your account.</Trans>
        </Typography>
        <Link2
          to={URLS.DEVICE_REMARKABLE}
          componentLocation={ComponentLocations.FRONT_PAGE.DASHBOARD}
          className="font-medium text-pen-blue"
        >
          <Trans>Manage devices</Trans>
          <ArrowRight />
        </Link2>
      </div>

      <FlexDivider className="mx-32 my-16 lxl:m-0" />

      <div className="flex w-full flex-1 flex-col justify-between gap-16 px-24">
        <Typography variant="heading-subtle-xl">
          <span className="flex items-center gap-8">
            <CloudUpload size={28} className="inline-block text-muted" />{' '}
            <Select
              value={
                hasEnterpriseSubscription ? 'connectEnterprise' : 'connect'
              }
              _connectEnterprise="Connect for Business"
              other="Connect"
            />
          </span>
        </Typography>
        {hasEnterpriseSubscription ? (
          <StatusTag status="Active" />
        ) : (
          <StatusTag subscription={subscription} />
        )}
        <Typography variant="body-sm" className="text-muted">
          <Trans>View your subscription details, payment and invoices.</Trans>
        </Typography>
        {canSeeEnterpriseBilling ? (
          <Link2
            to={URLS.ENTERPRISE_BILLING}
            componentLocation={ComponentLocations.FRONT_PAGE.DASHBOARD}
            className="font-medium text-pen-blue"
            target="_blank"
          >
            <Trans>Manage subscription</Trans>
            <ArrowRight />
          </Link2>
        ) : (
          <Link2
            to={URLS.SUBSCRIPTION}
            componentLocation={ComponentLocations.FRONT_PAGE.DASHBOARD}
            className="font-medium text-pen-blue"
          >
            <Trans>Manage subscription</Trans>
            <ArrowRight />
          </Link2>
        )}
      </div>

      <FlexDivider className="mx-32 my-16 lxl:m-0" />

      {featureFlags.EnableLinkToNewWebApp ? (
        <div
          className="flex w-full flex-1 flex-col gap-16 px-24"
          data-cy="feature-link-to-webapp"
        >
          <Typography variant="heading-subtle-xl">
            <span className="flex items-center gap-8">
              <MyFiles size={28} className="inline-block text-muted" />{' '}
              <Trans>Web app</Trans>
            </span>
          </Typography>
          <Tag variant="info-light">
            <Trans>Beta</Trans>
          </Tag>
          <Typography variant="body-sm" className="text-muted">
            <Trans>View, manage and upload files to your paper tablet.</Trans>
          </Typography>
          <LinkExternal
            to={EXTERNAL_URLS.WEBAPP_HOME}
            componentLocation={ComponentLocations.FRONT_PAGE.DASHBOARD}
            className="mt-auto font-medium text-pen-blue"
          >
            <Trans>Go to web app</Trans>
            <ArrowRight />
          </LinkExternal>
        </div>
      ) : (
        <div className="flex w-full flex-1 flex-col gap-16 px-24">
          <Typography variant="heading-subtle-xl">
            <span className="flex items-center gap-8">
              <Folder size={28} className="inline-block text-muted" />{' '}
              <Trans>My files</Trans>
            </span>
          </Typography>
          <Typography variant="body-sm" className="text-muted">
            <Trans>View, manage and upload files to your paper tablet.</Trans>
          </Typography>
          <Link2
            to={URLS.MYFILES}
            componentLocation={ComponentLocations.FRONT_PAGE.DASHBOARD}
            className="mt-auto font-medium text-pen-blue"
          >
            <Trans>Go to my files</Trans>
            <ArrowRight />
          </Link2>
        </div>
      )}
    </Sheet>
  );
};
